import { FC, useMemo, useState } from "react";
import { types, ModalProps, toast } from "@vilocnv/allsetra-core";
import { Form, Formik, FormikHelpers } from "formik";
import InnerForm from "./children/InnerForm";
import UpdateObjectRideConfirmationModal from "./children/UpdateObjectRideConfirmationModal";

// Data
import { isEmpty } from "lodash";
import {
  transformObjectRideDataForForm,
  updateObjectRideFormValidationSchema,
  OBJECT_RIDE_MODES_OPTIONS
} from "app/data/helpers";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  getObjectSpecificRideInfoThunk,
  updateObjectRideThunk
} from "app/features";
import {
  selectDrawerSelectedAccountId,
  selectAccountKeysState,
  selectAccountsState
} from "app/data/selectors";
import { SignalRService } from "app/data/services";
import { useTranslation } from "react-i18next";

type Props = Omit<ModalProps, "title" | "children"> & {
  objectRide: types.IObjectRide | null;
};

const UpdateObjectRideForm: FC<Props> = ({ open, onClose, objectRide }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(["translation"]);

  const accountId = useAppSelector(selectDrawerSelectedAccountId);
  const { accountKeys } = useAppSelector(selectAccountKeysState);
  const { usersBasicInfo } = useAppSelector(selectAccountsState);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] = useState<any>(null);

  const initialValues = useMemo(
    () =>
      !isEmpty(objectRide) ? transformObjectRideDataForForm(objectRide) : {},
    [objectRide]
  );

  const onFulfilledRequest = (
    params: any,
    formikHelpers: FormikHelpers<types.IUpdateObjectRide>
  ) => {
    formikHelpers.setSubmitting(false);
    setIsSubmitting(false);

    toast.success(t("toasts.objectRideUpdatedSuccess"));

    dispatch(getObjectSpecificRideInfoThunk(params));
  };

  const saveChangesHandler = async (
    values: types.IUpdateObjectRide & {
      startAddress: string;
      endAddress: string;
    },
    formikHelpers: FormikHelpers<types.IUpdateObjectRide>
  ) => {
    setFormDataToSubmit({ values, formikHelpers });
    setIsConfirmationModalOpen(true);
    formikHelpers.setSubmitting(false);
  };

  const handleConfirmationSubmit = async () => {
    if (!formDataToSubmit) return;

    const { values, formikHelpers } = formDataToSubmit;

    try {
      formikHelpers.setSubmitting(true);
      setIsSubmitting(true);
      setIsConfirmationModalOpen(false);

      const params: { accountId: string; objectId: string; rideId: string } = {
        accountId: accountId ?? "",
        objectId: objectRide?.object?.uniqueId ?? "",
        rideId: objectRide?.uniqueId ?? ""
      };

      const rideMode = Number(values.rideMode);
      const isBusinessOrCommute = rideMode === 1 || rideMode === 3;

      const data: any = {
        changeReason: values.changeReason,
        uniqueId: values.uniqueId,
        rideMode: rideMode,
        comments: values.comments || null,
        startAddressPointOfInterest: values.startAddressPointOfInterest || null,
        endAddressPointOfInterest: values.endAddressPointOfInterest || null,
        correctedEndAddress: values.correctedEndAddress || null,
        correctedEndLatitude: values.correctedEndLatitude || null,
        correctedEndLongitude: values.correctedEndLongitude || null,
        correctedStartAddress: values.correctedStartAddress || null,
        correctedStartLatitude: values.correctedStartLatitude || null,
        correctedStartLongitude: values.correctedStartLongitude || null
      };

      if (values.privateMileage) {
        data.privateMileage = Number(values.privateMileage);
      } else if (isBusinessOrCommute) {
        data.privateMileage = 0;
      }

      if (values.startAddress) {
        data.correctedStartAddress = values.startAddress;
      }
      if (values.endAddress) {
        data.correctedEndAddress = values.endAddress;
      }

      if (values.keyId) {
        data.keyId = values.keyId;
      } else if (values.driverId) {
        data.driverId = values.driverId;
      }

      // Clean up: remove null/undefined/empty values
      Object.keys(data).forEach((key) => {
        const value = data[key];
        if (
          value === undefined ||
          value === "" ||
          (value === null && key !== "comments")
        ) {
          delete data[key];
        }
      });

      const { type } = await dispatch(
        updateObjectRideThunk({ ...params, data })
      );

      if (type === "objects/updateObjectRideThunk/fulfilled") {
        // Set a timeout to stop listening after 10 seconds
        const timeoutId = setTimeout(() => {
          SignalRService.hubConnection?.off("EventRaised");
          onFulfilledRequest(params, formikHelpers);
          console.log("Stopped listening for EventRaised after 10 seconds.");
        }, 10000);

        const handleEventRaised = (event: any) => {
          if (event.eventName === types.BackendEventsEnum.RideUpdatedEvent) {
            onFulfilledRequest(params, formikHelpers);

            // Clear the timeout if the event is received
            clearTimeout(timeoutId);

            // Remove the listener if it's a one-time use
            SignalRService.hubConnection?.off("EventRaised", handleEventRaised);
          }
        };

        SignalRService.hubConnection?.on("EventRaised", handleEventRaised);
      } else {
        formikHelpers.setSubmitting(false);
        setIsSubmitting(false);
      }
    } catch (e: any) {
      setIsSubmitting(false);
    }
  };

  const handleConfirmationCancel = () => {
    setIsConfirmationModalOpen(false);
    setFormDataToSubmit(null);
  };

  // Get labels for confirmation modal
  const getDriverEmail = (driverId: string | undefined) => {
    if (!driverId) return undefined;
    const driver = usersBasicInfo?.find(
      (user: any) => user.uniqueId === driverId
    );
    return driver?.email;
  };

  const getKeyName = (keyId: string | undefined) => {
    if (!keyId) return undefined;
    const key = accountKeys?.find((key: any) => key.uniqueId === keyId) as any;
    return key?.keyName;
  };

  const getRideModeLabel = (rideMode: number | undefined) => {
    if (rideMode === undefined) return undefined;
    const modes = OBJECT_RIDE_MODES_OPTIONS(t);
    const mode = modes.find((m: any) => m.value === rideMode);
    return mode?.label;
  };

  return (
    <>
      <Formik
        // @ts-ignore
        initialValues={initialValues}
        validationSchema={updateObjectRideFormValidationSchema(t)}
        // @ts-ignore
        onSubmit={saveChangesHandler}
        enableReinitialize
        validateOnMount
      >
        <Form>
          <InnerForm
            open={open}
            onClose={onClose}
            customIsSubmitting={isSubmitting}
            objectRide={objectRide}
          />
        </Form>
      </Formik>

      <UpdateObjectRideConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfirmationCancel}
        onConfirm={handleConfirmationSubmit}
        formData={formDataToSubmit?.values || null}
        driverEmail={getDriverEmail(formDataToSubmit?.values?.driverId)}
        keyName={getKeyName(formDataToSubmit?.values?.keyId)}
        rideModeLabel={getRideModeLabel(formDataToSubmit?.values?.rideMode)}
      />
    </>
  );
};

export default UpdateObjectRideForm;
